import React, { useState, useMemo, useEffect, useRef } from "react";
import Avatar from "../Avatar";

export default function CoachSearchModal({
	visible,
	onClose,
	students = [],
	user,
	exercises = [],
	coachTemplates = [],
	onSelectStudent,
	onSelectExercise,
	onSelectTemplate,
}) {
	const [query, setQuery] = useState("");
	const [activeIndex, setActiveIndex] = useState(0);
	const inputRef = useRef(null);
	const listRef = useRef(null);

	useEffect(() => {
		if (visible) {
			setQuery("");
			setActiveIndex(0);
			setTimeout(() => inputRef.current?.focus(), 30);
		}
	}, [visible]);

	const allStudents = useMemo(() => {
		const list = [];
		if (user && user.id) {
			list.push({ id: user.id, prenom: user.prenom, nom: user.nom, photoProfil: user.photoProfil, isSelf: true });
		}
		(students || []).forEach((s) => list.push(s));
		return list;
	}, [students, user]);

	const results = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) {
			return [
				...allStudents.slice(0, 5).map((s) => ({
					type: "student",
					key: `s-${s.id}`,
					label: `${s.prenom} ${s.nom}${s.isSelf ? " (moi)" : ""}`,
					payload: s,
				})),
			];
		}
		const out = [];
		allStudents.forEach((s) => {
			if ((`${s.prenom || ""} ${s.nom || ""}`).toLowerCase().includes(q)) {
				out.push({ type: "student", key: `s-${s.id}`, label: `${s.prenom} ${s.nom}${s.isSelf ? " (moi)" : ""}`, payload: s });
			}
		});
		(exercises || []).forEach((ex) => {
			if ((ex.nomExercice || "").toLowerCase().includes(q)) {
				out.push({ type: "exercise", key: `e-${ex.id}`, label: ex.nomExercice, payload: ex });
			}
		});
		(coachTemplates || []).forEach((t) => {
			const name = t.Nom || t.nom;
			if ((name || "").toLowerCase().includes(q)) {
				out.push({ type: "template", key: `t-${t.Id || t.id}`, label: name, payload: t });
			}
		});
		return out.slice(0, 30);
	}, [query, allStudents, exercises, coachTemplates]);

	useEffect(() => {
		setActiveIndex(0);
	}, [query]);

	useEffect(() => {
		if (!visible) return;
		const onKey = (e) => {
			if (e.key === "Escape") {
				onClose();
			} else if (e.key === "ArrowDown") {
				e.preventDefault();
				setActiveIndex((i) => Math.min(results.length - 1, i + 1));
			} else if (e.key === "ArrowUp") {
				e.preventDefault();
				setActiveIndex((i) => Math.max(0, i - 1));
			} else if (e.key === "Enter") {
				e.preventDefault();
				const r = results[activeIndex];
				if (r) selectResult(r);
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visible, results, activeIndex]);

	useEffect(() => {
		if (!listRef.current) return;
		const el = listRef.current.querySelector(`[data-idx="${activeIndex}"]`);
		if (el) el.scrollIntoView({ block: "nearest" });
	}, [activeIndex]);

	const selectResult = (r) => {
		if (r.type === "student" && onSelectStudent) onSelectStudent(r.payload);
		if (r.type === "exercise" && onSelectExercise) onSelectExercise(r.payload);
		if (r.type === "template" && onSelectTemplate) onSelectTemplate(r.payload);
		onClose();
	};

	if (!visible) return null;

	return (
		<div className="coach-search-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
			<div className="coach-search-modal">
				<div className="coach-search-input-wrap">
					<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
						<circle cx="11" cy="11" r="8" />
						<line x1="21" y1="21" x2="16.65" y2="16.65" />
					</svg>
					<input
						ref={inputRef}
						type="text"
						className="coach-search-input"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder="Rechercher un élève, un exercice, un modèle…"
					/>
					<kbd className="coach-search-kbd">ESC</kbd>
				</div>
				<div ref={listRef} className="coach-search-results">
					{results.length === 0 ? (
						<div className="coach-search-empty">Aucun résultat</div>
					) : (
						results.map((r, idx) => (
							<button
								key={r.key}
								data-idx={idx}
								className={`coach-search-result ${idx === activeIndex ? "active" : ""}`}
								onMouseEnter={() => setActiveIndex(idx)}
								onClick={() => selectResult(r)}
							>
								{r.type === "student" && (
									<Avatar prenom={r.payload.prenom} nom={r.payload.nom} photo={r.payload.photoProfil} size={28} />
								)}
								{r.type !== "student" && (
									<span className={`coach-search-result-icon ${r.type}`}>
										{r.type === "exercise" ? "💪" : "📋"}
									</span>
								)}
								<span className="coach-search-result-label">{r.label}</span>
								<span className="coach-search-result-type">{r.type === "student" ? "Élève" : r.type === "exercise" ? "Exercice" : "Modèle"}</span>
							</button>
						))
					)}
				</div>
			</div>
		</div>
	);
}
